// Leaflet map with the RIVERGHOST spy skin. Leaflet is dynamically imported
// so the ~42KB stays out of the boot bundle (guests on island cell data).
import type * as Leaflet from "leaflet";

export type LeafletNS = typeof Leaflet;

export const DONAUINSEL: [number, number] = [48.229, 16.412];

let leafletPromise: Promise<LeafletNS> | null = null;

export function loadLeaflet(): Promise<LeafletNS> {
  if (!leafletPromise) {
    leafletPromise = Promise.all([
      import("leaflet"),
      // Vite injects the stylesheet on dynamic import.
      import("leaflet/dist/leaflet.css"),
    ]).then(([m]) => (m as unknown as { default?: LeafletNS }).default ?? (m as unknown as LeafletNS));
  }
  return leafletPromise;
}

export interface SpyMap {
  L: LeafletNS;
  map: Leaflet.Map;
}

/**
 * Mount a dark-skinned OSM map into `container`. The container gets the
 * `.spymap` class (height via CSS; override inline if needed).
 */
export async function spyMap(
  container: HTMLElement,
  opts: { center?: [number, number]; zoom?: number } = {},
): Promise<SpyMap> {
  const L = await loadLeaflet();
  container.classList.add("spymap");
  const map = L.map(container, { zoomControl: false }).setView(
    opts.center ?? DONAUINSEL,
    opts.zoom ?? 14,
  );
  L.control.zoom({ position: "bottomright" }).addTo(map);
  L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
    maxZoom: 19,
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>',
  }).addTo(map);
  return { L, map };
}

/** Small glowing dot marker (enemy blips, own position). */
export function blipIcon(L: LeafletNS, color: string): Leaflet.DivIcon {
  return L.divIcon({
    className: "",
    html: `<div class="map-blip" style="--blip:${color}"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });
}

/** Live-agent blip with a codename label — every guest is visible by default. */
export function agentIcon(L: LeafletNS, codename: string, color = "var(--current)"): Leaflet.DivIcon {
  const safe = codename.replace(/[<>&]/g, "").slice(0, 16) || "AGENT";
  return L.divIcon({
    className: "",
    html:
      `<div class="map-agent"><span class="map-blip" style="--blip:${color}"></span>` +
      `<span class="map-agent__tag">${safe}</span></div>`,
    iconSize: [90, 14],
    iconAnchor: [7, 7],
  });
}

/** Emoji pin marker (caches, chapters). Dim it via opts for locked state. */
export function pinIcon(
  L: LeafletNS,
  emoji: string,
  opts: { dim?: boolean; ring?: string } = {},
): Leaflet.DivIcon {
  const ring = opts.ring ?? "var(--amber)";
  return L.divIcon({
    className: "",
    html: `<div class="map-pin${opts.dim ? " map-pin--dim" : ""}" style="--ring:${ring}">${emoji}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });
}

export type LatLng = { lat: number; lng: number };

/** Ray-casting point-in-polygon (good enough at island scale). */
export function pointInPolygon(p: LatLng, pts: LatLng[]): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i], b = pts[j];
    if (
      a.lng > p.lng !== b.lng > p.lng &&
      p.lat < ((b.lat - a.lat) * (p.lng - a.lng)) / (b.lng - a.lng) + a.lat
    ) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * Operation-zone handling shared by every guest map: draws the admin-defined
 * polygon, auto-zooms to it, and toggles an "outside the zone" chip on the
 * wrapper whenever the live game is on and the agent has wandered out.
 * The wrapper must be position:relative.
 */
export function areaController(sm: SpyMap, wrapper: HTMLElement) {
  let polygon: Leaflet.Polygon | null = null;
  let points: LatLng[] | null = null;
  let live = false;
  let pos: LatLng | null = null;

  const chip = document.createElement("div");
  chip.className = "map-warn";
  chip.textContent = "⚠ OUTSIDE THE OPERATION ZONE, AGENT";
  chip.style.display = "none";
  wrapper.append(chip);

  function refresh() {
    const out = live && points && points.length >= 3 && pos && !pointInPolygon(pos, points);
    chip.style.display = out ? "block" : "none";
  }

  return {
    setArea(pts: LatLng[] | null | undefined) {
      points = pts ?? null;
      polygon?.remove();
      polygon = null;
      if (points && points.length >= 3) {
        polygon = sm.L.polygon(points.map((p) => [p.lat, p.lng] as [number, number]), {
          color: "var(--green)",
          weight: 1.5,
          fillColor: "var(--green)",
          fillOpacity: 0.05,
        })
          .addTo(sm.map)
          .bindPopup("OPERATION ZONE");
        sm.map.fitBounds(polygon.getBounds(), { padding: [24, 24] });
      }
      refresh();
    },
    setLive(l: boolean) { live = l; refresh(); },
    setPos(p: LatLng) { pos = p; refresh(); },
  };
}

/**
 * Deterministic rough search zone derived from a cache's exact coordinates:
 * the true point lies inside the circle but off-centre (40–120 m, direction
 * hashed from the id) so the zone doesn't give the spot away. Mirrored in
 * scripts/seed-content.ts — keep the formula in sync.
 */
export function roughZoneFor(
  id: string,
  lat: number,
  lng: number,
  radius = 250,
): { roughLat: number; roughLng: number; roughRadius: number } {
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  const angle = ((h % 360) * Math.PI) / 180;
  // Offset the displayed centre by up to half the radius so the true pin
  // sits inside but is not exactly centred (keeps it a real search zone).
  const maxOffset = Math.min(radius * 0.45, 80);
  const dist = maxOffset * 0.5 + ((h % 100) / 100) * maxOffset * 0.5;
  const dLat = (dist * Math.cos(angle)) / 111320;
  const dLng = (dist * Math.sin(angle)) / (111320 * Math.cos((lat * Math.PI) / 180));
  return {
    roughLat: +(lat + dLat).toFixed(6),
    roughLng: +(lng + dLng).toFixed(6),
    roughRadius: radius,
  };
}

// ---- inline SVG icons for map pins ----------------------------------------

export const GUN_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 20" width="22" height="16" fill="currentColor">
  <rect x="0" y="8" width="15" height="4.5" rx="1.5"/>
  <path d="M13 4.5h12a2 2 0 012 2v7a2 2 0 01-2 2H13V4.5z"/>
  <rect x="16" y="2" width="7" height="3.5" rx="1.4"/>
  <rect x="15" y="13.5" width="4" height="5.5" rx="1.4"/>
</svg>`;

export const SUPPLY_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 22 20" width="20" height="18" fill="currentColor">
  <rect x="1" y="7" width="20" height="13" rx="2"/>
  <rect x="0" y="5" width="22" height="4.5" rx="1.5" fill-opacity="0.75"/>
  <rect x="10.5" y="7" width="1.5" height="13" fill="rgba(0,0,0,0.22)" rx="0.5"/>
  <rect x="1" y="13" width="20" height="1.5" fill="rgba(0,0,0,0.22)" rx="0.5"/>
</svg>`;

/** Badge at the centre of a rough search zone. */
export function tierIcon(L: LeafletNS, tier: number, kind: "gun" | "powerup" = "gun"): Leaflet.DivIcon {
  const t = Math.max(1, Math.min(5, tier));
  const dots = Array.from({ length: 5 }, (_, i) =>
    `<span style="opacity:${i < t ? "1" : "0.2"};font-size:7px;line-height:1">●</span>`
  ).join("");
  const isSupply = kind === "powerup";
  const html = isSupply
    ? `<div class="map-tier map-tier--supply">${SUPPLY_SVG}<span style="margin-left:4px;font-size:9px;letter-spacing:.1em">SUPPLY</span></div>`
    : `<div class="map-tier map-tier--gun">${GUN_SVG}<span style="margin-left:5px;display:flex;gap:1px">${dots}</span></div>`;
  return L.divIcon({
    className: "",
    html,
    iconSize: [90, 26],
    iconAnchor: [45, 13],
  });
}

/**
 * Shared cache layer used by the tactical map and the caches screen.
 * Per cache: exact pin when I revealed it or someone claimed it (published),
 * otherwise a rough amber search zone with a power-tier badge.
 */
export function drawCacheLayer(
  sm: SpyMap,
  layer: Leaflet.LayerGroup,
  caches: Array<{
    id: string; name: string; powerTier: number; firstClaimedBy: string | null;
    kind?: "gun" | "powerup";
    claimPhotos?: Record<string, string>;
    lat?: number; lng?: number;
    roughLat?: number; roughLng?: number; roughRadius?: number;
  }>,
  progress: Record<string, { status?: string; locationRevealed?: boolean; lat?: number; lng?: number }>,
  onSelect?: (cacheId: string) => void,
): void {
  layer.clearLayers();
  for (const c of caches) {
    const prog = progress[c.id];
    const kind = c.kind === "powerup" ? "powerup" : "gun";
    const zoneColor = kind === "powerup" ? "var(--green)" : "var(--amber)";
    // SVG icon with color set via wrapper color
    const iconSvg = kind === "powerup"
      ? `<span style="color:var(--green)">${SUPPLY_SVG}</span>`
      : `<span style="color:var(--amber)">${GUN_SVG}</span>`;
    const exact =
      prog?.locationRevealed && typeof prog.lat === "number" && typeof prog.lng === "number"
        ? { lat: prog.lat, lng: prog.lng }
        : typeof c.lat === "number" && typeof c.lng === "number"
          ? { lat: c.lat, lng: c.lng }
          : null;

    if (exact) {
      const mine = prog?.status === "claimed";
      const claimed = !!c.firstClaimedBy;
      const photos = Object.values(c.claimPhotos ?? {});
      const photoHtml = photos.length && photos[0].startsWith("http")
        ? `<br><img src="${photos[0]}" style="width:120px;border-radius:6px;margin-top:6px">`
        : "";
      const m = sm.L.marker([exact.lat, exact.lng], {
        icon: pinIcon(sm.L, iconSvg, {
          ring: mine ? "var(--green)" : claimed ? "var(--cream-dim)" : "var(--amber)",
          dim: claimed && !mine,
        }),
      }).addTo(layer);
      if (onSelect) m.on("click", () => onSelect(c.id));
      else m.bindPopup(`${c.name}${claimed ? " · CLAIMED" : ""}${mine ? " (yours)" : ""}${photoHtml}`);
    } else if (typeof c.roughLat === "number" && typeof c.roughLng === "number") {
      // Rough search zone: the stash is somewhere in here. Solve or pay to pin it.
      const zone = sm.L.circle([c.roughLat, c.roughLng], {
        radius: c.roughRadius ?? 250,
        color: zoneColor,
        weight: 1.5,
        dashArray: "6 6",
        fillColor: zoneColor,
        fillOpacity: 0.07,
      }).addTo(layer);
      const badge = sm.L.marker([c.roughLat, c.roughLng], { icon: tierIcon(sm.L, c.powerTier, kind) })
        .addTo(layer);
      if (onSelect) {
        zone.on("click", () => onSelect(c.id));
        badge.on("click", () => onSelect(c.id));
      } else {
        badge.bindPopup(`${c.name} — search zone. Crack the riddle (or spend intel) to pin it.`);
      }
    }
  }
}
